import { useCommonUseOfFundsList } from "@boundless/bl-api-client/common/services"
import { WhenFundsNeededEnumOptions } from "@boundless/bl-api-client/customer/models/WhenFundsNeededEnumLabels"
import { useCustomerPrequalifyingFundingNeedsUpdate } from "@boundless/bl-api-client/customer/services"
import { CustomerPrequalifyingFundingNeedsUpdateBody } from "@boundless/bl-api-client/customer/services.zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"

import { FormMoneyInput } from "@/components/form-elements/FormMoneyInput"
import { FormSelect } from "@/components/form-elements/FormSelect"
import { OnboardingFormStep } from "@/components/onboarding/OnboardingFormStep"
import { type ErrorType } from "@/lib/clients"
import { onboardingFundingErrorMap } from "@/lib/errors"
import { applyErrorsToForm, type ErrorResponseType } from "@/lib/forms/utils"

import type { z } from "zod"

/**
 * The row this step writes, with one departure from the endpoint's schema: it
 * rejects a zero capital amount, which is not an answer worth asking a
 * borrower to give.
 */
const schema = CustomerPrequalifyingFundingNeedsUpdateBody.extend({
  capitalAmount:
    CustomerPrequalifyingFundingNeedsUpdateBody.shape.capitalAmount.positive(),
})

export type FundingAnswers = z.infer<typeof schema>

/**
 * `capitalAmount` is deliberately undefined rather than 0, so the field shows
 * its placeholder instead of a "$0" that reads like a choice already made.
 */
const EMPTY = {
  capitalAmount: undefined,
  useOfFunds: undefined,
  whenFundsNeeded: "" as FundingAnswers["whenFundsNeeded"],
}

/** What the use of funds select says while it has nothing to offer. */
function useOfFundsPlaceholder(isPending: boolean, hasError: boolean) {
  if (isPending) return "Loading…"
  if (hasError) return "Couldn't load these — please refresh"
  return "Select a use of funds"
}

interface Props {
  companyUuid: string
  /**
   * What is already in this step: the answer saved on an earlier pass, or the
   * draft left behind by walking away from it mid-answer. Partial, because a
   * draft is whatever had been typed at the moment Back was pressed.
   */
  answers: Partial<FundingAnswers> | null
  /** Handed the answers and the match count the save came back with. */
  onNext: (answers: FundingAnswers, matchCount: number | null) => void
  /** Handed whatever is in the fields, valid or not, so it survives the trip. */
  onBack: (draft: Partial<FundingAnswers>) => void
}

/**
 * What the borrower wants and when — the `PrequalifyingFundingNeeds` row,
 * written on its way out.
 *
 * Saving it is also what yields the lender match count, which the reveal at
 * the end of the wizard is built from.
 */
export function FundingStep({ companyUuid, answers, onNext, onBack }: Props) {
  const {
    data: useOfFunds,
    isPending: useOfFundsPending,
    error: useOfFundsError,
  } = useCommonUseOfFundsList()
  const { mutateAsync: saveFundingNeeds } =
    useCustomerPrequalifyingFundingNeedsUpdate()

  const form = useForm<FundingAnswers>({
    resolver: zodResolver(schema, { errorMap: onboardingFundingErrorMap }),
    defaultValues: answers ?? EMPTY,
  })

  // Keyed by id rather than by a stringified copy of it, so the select reads
  // and writes the same number the row stores.
  const useOfFundsOptions = (useOfFunds ?? []).map((option) => ({
    id: option.id,
    label: option.name,
  }))

  const submit = form.handleSubmit(async (values) => {
    try {
      const saved = await saveFundingNeeds({ companyUuid, data: values })
      onNext(values, saved.lenderMatchCount)
    } catch (error) {
      // Whatever the server blames on a field lands under that field; the rest
      // arrives as the root error the step renders at its foot.
      applyErrorsToForm(form.setError, error as ErrorType<ErrorResponseType>)
    }
  })

  return (
    <OnboardingFormStep
      form={form}
      onSubmit={submit}
      title="Quick snapshot"
      description="A few numbers so we can start matching you with lenders."
      label="Finish"
      // Raw values, straight out of the fields: going back is not a submission,
      // so the resolver never runs and a half-filled step is kept as it stands.
      onBack={() => onBack(form.getValues())}
    >
      <FormMoneyInput<FundingAnswers>
        name="capitalAmount"
        label="How much funding are you looking for?"
        placeholder="250,000"
      />

      {/* Held shut until the options land: FormSelect sizes its popover from
          `options.length`, so an empty list opens to nothing at all. */}
      <FormSelect<FundingAnswers>
        name="useOfFunds"
        options={useOfFundsOptions}
        parseValueAsInt
        disabled={useOfFundsOptions.length === 0}
        label="What will you use it for?"
        placeholder={useOfFundsPlaceholder(
          useOfFundsPending,
          !!useOfFundsError,
        )}
      />

      <FormSelect<FundingAnswers>
        name="whenFundsNeeded"
        options={WhenFundsNeededEnumOptions}
        label="When do you need it?"
        placeholder="When do you need it"
      />
    </OnboardingFormStep>
  )
}
